import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../site/auth";
import { useSeo } from "../../site/ui";
import { DEPOSIT, FREE_KRW, LAONA, SELFPROTO } from "../data";
import { RecipeCard, recipeApi } from "./LaonaRecipe";
import { useLaonaService, WayCards } from "./LaonaWays";

/* =========================================================
   라오나 키트 (/laonabot) — 맨 위에 두 가지 방법(직접 만들기 · 라오나봇과 만들기)을 좌우로,
   그 아래에 라오나봇과 만드는 방법(대화 → 설계 → 키트)을 자세히 보여 준다.
   "라오나봇과 대화로 설계하면, 쉽게 조립할 수 있는 시제품이 도착한다." (SELFPROTO_CONCEPT.md)
   ========================================================= */

/* 사용자가 겪는 순서 (콘셉트 §5) */
const STEPS = [
  { no: "01", title: "대화로 기능 정리", desc: "만들고 싶은 것을 말하면 라오나봇이 질문으로 '무엇이, 어떤 입력에, 어떻게 반응하는지'를 기능 명세로 정리합니다." },
  { no: "02", title: "모듈 자동 선정", desc: "검증된 모듈 카탈로그 안에서 알맞은 모듈을 고르고, 부품 목록과 예상 가격을 보여 줍니다." },
  { no: "03", title: "자동 배치 · 배선", desc: "모듈을 브레드보드 위에 배치하고 핀을 이어 배선도를 만듭니다. 화면에서 바로 확인합니다." },
  { no: "04", title: "내 프로젝트로 저장", desc: "기능 명세 · 배선도를 확인하고 내 프로젝트로 저장합니다. 이때 설계가 확정되고, 명세가 펌웨어 기능정의서가 됩니다." },
  { no: "05", title: "주문 · 펌웨어 개발", desc: "부품과 펌웨어 개발 · 빌드를 함께 주문합니다. 입금이 확인되면 아이닷메타가 기능정의서대로 펌웨어를 만듭니다. 코드를 몰라도 됩니다." },
  { no: "06", title: "조립 · 업로드", desc: "키트가 도착하면 배선도대로 꽂고 펌웨어를 올립니다. 펌웨어는 바이너리를 포함해 코드 전체를 드립니다." },
];

const WHO = [
  ["아이디어가 있는 예비창업자", "투자 · 공모전 · 펀딩에서 보여 줄 동작 데모"],
  ["교사 · 학원 · 메이커스페이스", "수업에서 똑같이 다시 만들 수 있는 결과물과 반복 주문 키트"],
  ["취미 메이커", "회로 · 코드 부담 없이 만들고 싶은 것 만들기"],
  ["전시 · 공간 · 인테리어 제작자", "인터랙티브 소품의 빠른 시제품"],
];

const PROMISES = [
  ["검증된 모듈만 씁니다", "아이닷메타가 핀 배열과 동작을 확인한 모듈 카탈로그 안에서만 설계합니다."],
  ["확정은 사용자가 합니다", "명세 · 배선도 · 가격은 자동으로 확정되지 않습니다. 보고 승인해야 진행됩니다."],
  ["범위를 분명히 말합니다", "만들 수 있는 것 · 없는 것 · 별도 상담이 필요한 것을 라오나봇이 먼저 알려 드립니다."],
  ["배선 실수를 줄입니다", "컬러 배선도 · 핀 라벨로 조립 오류를 줄이는 것까지 서비스의 몫입니다."],
];

const FAQ = [
  ["회로나 코딩을 전혀 몰라도 되나요?", "네. 만들고 싶은 것을 말로 설명하면 됩니다. 배선은 배선도대로 꽂기만 하면 되고, 펌웨어는 만들어 드립니다."],
  ["비용은 어떻게 계산되나요?", `직접 만들기는 무료이고 부품값과 배송비(5만 원 이상 무료)만 냅니다. 라오나봇과 만들기는 무료로 시작하고, 대화 · 설계 사용량이 ${FREE_KRW.toLocaleString("ko-KR")}원을 넘으면 이용료 ${DEPOSIT.toLocaleString("ko-KR")}원을 결제하고 이어서 진행합니다. 설계를 마치면 부품과 펌웨어 개발 · 빌드(기본 5만 원, 작업량에 따라 추가 비용이 최대 10만 원까지 발생할 수 있지만 일반적으로는 없음)를 함께 주문합니다. 대화 · 설계에 쓴 사용료는 따로 받지 않습니다.`],
  ["어떤 보드를 쓰나요?", "ESP32 계열이 기본이고, 기능에 따라 아두이노 · 라즈베리파이 피코 등 카탈로그의 다른 MCU 보드도 씁니다."],
  ["카탈로그에 없는 기능이 필요하면요?", "라오나봇이 가능 여부를 먼저 알려 드립니다. 카탈로그 밖의 개발이나 양산은 아이닷메타 개발 상담으로 이어서 진행할 수 있습니다."],
  ["만든 코드를 받을 수 있나요?", "네. 펌웨어 개발 · 빌드를 주문하면 바이너리를 포함해 코드 전체를 드립니다."],
];

/* 히어로 그림 — 서비스 흐름 한 장면: ① 라오나봇과 대화 → ② 브레드보드 설계 · 배선 → ③ 코드 ZIP + 시제품 키트.
   순서대로 나타난다 (움직임 줄이기 설정이면 처음부터 다 보인다). 색은 테마 변수 */
function HeroDiagram() {
  const T = (x, y, text, cls, extra = {}) => <text x={x} y={y} className={cls} {...extra}>{text}</text>;
  /* 브레드보드 위 모듈 — 아래쪽에 핀 줄 */
  const mods = [
    { x: 38, y: 226, w: 78, h: 44, name: "OLED", sub: "화면" },
    { x: 38, y: 318, w: 78, h: 44, name: "SOIL", sub: "수분 센서" },
    { x: 262, y: 262, w: 78, h: 44, name: "RELAY", sub: "펌프 스위치" },
  ];
  const wires = [
    ["M116 238H136V252H154", "--lb-w-red"],
    ["M116 250H130V268H154", "--lb-w-black"],
    ["M116 262H126V284H154", "--lb-w-blue"],
    ["M116 330H128V300H154", "--lb-w-green"],
    ["M116 344H136V316H154", "--lb-w-black"],
    ["M234 268H248V274H262", "--lb-w-amber"],
    ["M234 292H248V294H262", "--lb-w-red"],
  ];
  return (
    <svg className="lb-hero__svg" viewBox="0 0 560 420" role="img"
         aria-label="라오나봇과 대화로 기능을 정하면 브레드보드 배선이 설계되고, 코드 ZIP 과 시제품 키트가 도착하는 흐름">
      <defs>
        <pattern id="lb-grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M20 0H0V20" fill="none" stroke="var(--lb-grid)" strokeWidth="1" />
        </pattern>
        <pattern id="lb-holes" width="10" height="10" patternUnits="userSpaceOnUse">
          <circle cx="5" cy="5" r="1.3" fill="var(--lb-line)" />
        </pattern>
        <marker id="lb-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M0 0L10 5L0 10z" fill="var(--lb-accent)" />
        </marker>
      </defs>
      <rect width="560" height="420" rx="14" fill="var(--lb-canvas)" />
      <rect width="560" height="420" rx="14" fill="url(#lb-grid)" />

      {/* ① 대화 */}
      <g className="lb-pop" style={{ "--d": "0.1s" }}>
        {T(24, 22, "01 대화", "lb-hero__step")}
        <rect x="20" y="32" width="300" height="140" rx="12" className="lb-hero__mod" />
        <rect x="32" y="44" width="18" height="15" rx="4" fill="var(--lb-accent)" />
        <circle cx="38" cy="51" r="2" fill="var(--lb-accent-ink)" />
        <circle cx="44" cy="51" r="2" fill="var(--lb-accent-ink)" />
        {T(58, 56, "라오나봇", "lb-hero__name lb-hero__name--sm")}
      </g>
      <g className="lb-pop" style={{ "--d": "0.5s" }}>
        <rect x="118" y="70" width="190" height="28" rx="10" fill="var(--lb-accent-soft)" />
        {T(213, 88, "흙이 마르면 물을 주고 싶어요", "lb-hero__chat", { textAnchor: "middle" })}
      </g>
      <g className="lb-pop" style={{ "--d": "1.1s" }}>
        <rect x="32" y="108" width="222" height="52" rx="10" fill="var(--lb-canvas)" stroke="var(--lb-line)" />
        {T(44, 129, "수분 센서 · 펌프 · OLED 로", "lb-hero__chat")}
        {T(44, 148, "설계해 둘게요 ✓", "lb-hero__chat")}
      </g>

      {/* 대화 → 설계 */}
      <path d="M170 176V196" className="lb-hero__flow lb-pop" style={{ "--d": "1.5s" }} markerEnd="url(#lb-arrow)" />

      {/* ② 설계 · 배선 — 브레드보드 */}
      <g className="lb-pop" style={{ "--d": "1.6s" }}>
        {T(24, 198, "02 설계 · 배선", "lb-hero__step")}
        <rect x="20" y="206" width="334" height="194" rx="10" className="lb-hero__mod" />
        <rect x="30" y="214" width="314" height="178" fill="url(#lb-holes)" />
        <path d="M30 216H344" stroke="var(--lb-w-red)" strokeWidth="1.5" opacity=".7" />
        <path d="M30 390H344" stroke="var(--lb-w-blue)" strokeWidth="1.5" opacity=".7" />
        <path d="M30 303H344" stroke="var(--lb-line)" strokeWidth="5" />
        {mods.map((m) => (
          <g key={m.name}>
            <rect x={m.x} y={m.y} width={m.w} height={m.h} rx="6" className="lb-hero__mod" />
            {T(m.x + 9, m.y + 19, m.name, "lb-hero__name lb-hero__name--sm")}
            {T(m.x + 9, m.y + 34, m.sub, "lb-hero__sub")}
          </g>
        ))}
        <rect x="154" y="226" width="80" height="140" rx="8" className="lb-hero__mod" />
        <rect x="174" y="276" width="40" height="40" rx="3" className="lb-hero__chip" />
        {T(164, 246, "ESP32", "lb-hero__name lb-hero__name--sm")}
        {[252, 268, 284, 300, 316].map((y) => <circle key={y} cx="154" cy={y} r="3" className="lb-hero__pin" />)}
        {[274, 294].map((y) => <circle key={y} cx="234" cy={y} r="3" className="lb-hero__pin" />)}
      </g>
      {wires.map(([d, color], i) => (
        <path key={d} d={d} className="lb-wire" style={{ "--d": `${2 + i * 0.12}s`, stroke: `var(${color})` }} />
      ))}

      {/* 설계 → 결과물 */}
      <path d="M356 250C372 250 368 120 384 120" className="lb-hero__flow lb-pop" style={{ "--d": "2.9s" }} markerEnd="url(#lb-arrow)" />
      <path d="M356 330C368 330 372 318 384 318" className="lb-hero__flow lb-pop" style={{ "--d": "3.1s" }} markerEnd="url(#lb-arrow)" />

      {/* ③ 코드 ZIP */}
      <g className="lb-pop" style={{ "--d": "3.2s" }}>
        {T(388, 22, "03 코드 + 키트", "lb-hero__step")}
        <path d="M388 32H516L540 56V172H388Z" className="lb-hero__mod" />
        <path d="M516 32V56H540" fill="none" stroke="var(--lb-line)" strokeWidth="1.5" />
        {T(400, 56, "project.zip", "lb-hero__name lb-hero__name--sm")}
        {[["</>", "펌웨어 · 코드"], ["▦", "배선도"], ["≡", "부품 목록"]].map(([icon, label], i) => (
          <g key={label}>
            <rect x="400" y={72 + i * 32} width="22" height="22" rx="5" fill="var(--lb-accent-soft)" />
            {T(411, 87 + i * 32, icon, "lb-hero__icon", { textAnchor: "middle" })}
            {T(430, 87 + i * 32, label, "lb-hero__chat")}
          </g>
        ))}
      </g>

      {/* ③ 시제품 키트 상자 */}
      <g className="lb-pop" style={{ "--d": "3.5s" }}>
        <path d="M388 246L416 222H540L512 246Z" fill="var(--lb-accent-soft)" stroke="var(--lb-accent)" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M512 246L540 222V370L512 396Z" fill="var(--lb-panel)" stroke="var(--lb-accent)" strokeWidth="1.5" strokeLinejoin="round" />
        <rect x="388" y="246" width="124" height="150" fill="var(--lb-panel)" stroke="var(--lb-accent)" strokeWidth="1.5" />
        {/* 포장 테이프 — 글자와 겹치지 않게 오른쪽에 */}
        <rect x="490" y="246" width="14" height="150" fill="var(--lb-accent)" opacity=".85" />
        <path d="M490 246L518 222H532L504 246Z" fill="var(--lb-accent)" opacity=".85" />
        {T(398, 276, "시제품 키트", "lb-hero__name lb-hero__name--sm")}
        <rect x="398" y="330" width="34" height="34" rx="3" fill="none" stroke="var(--lb-ink)" strokeWidth="1.5" />
        <path d="M403 335h8v8h-8zM419 335h8v8h-8zM403 351h8v8h-8zM417 349h4v4h-4zM423 355h4v4h-4z" fill="var(--lb-ink)" />
        {T(398, 384, "모듈 · 브레드보드", "lb-hero__sub")}
      </g>
    </svg>
  );
}

function Recipes() {
  const [state, setState] = useState({ status: "loading", items: [] });
  useEffect(() => {
    let alive = true;
    recipeApi("?limit=6")
      .then((d) => alive && setState({ status: "ready", items: d.items }))
      .catch(() => alive && setState({ status: "error", items: [] }));
    return () => { alive = false; };
  }, []);
  if (state.status === "loading") return <p className="lb-empty">레시피를 불러오는 중입니다…</p>;
  if (state.status === "error") return <p className="lb-empty">레시피를 불러오지 못했습니다.</p>;
  if (!state.items.length) {
    return (
      <div className="lb-empty lb-empty--box">
        <b>아직 공개된 레시피가 없습니다</b>
        <p>회원이 내 프로젝트에서 배선까지 마친 시제품을 공개하면 이곳에 올라옵니다.</p>
      </div>
    );
  }
  return (
    <div className="lb-cards">
      {state.items.map((r) => <RecipeCard key={r.id} recipe={r} />)}
    </div>
  );
}

export default function LaonaHome() {
  const { member } = useAuth();
  useSeo({
    title: "LaonaBot 라오나봇 | 대화로 설계하는 나만의 시제품 — I.META",
    description:
      "라오나봇과 대화로 원하는 기능을 설계하면, 브레드보드에 바로 꽂아 조립하는 시제품 키트와 동작하는 펌웨어를 받습니다. 아이닷메타(I.META)의 시제품 서비스.",
    path: "/laonabot",
  });
  /* AI 설계 서비스가 멈춰 있으면(관리자 제외) 아래 라오나봇 설명에서도 시작 버튼을 감춘다 */
  const svc = useLaonaService() || { ai: true };

  return (
    <>
      <section className="lb-top">
        <div className="lb-wrap">
          <header className="lb-head lb-top__head">
            <p className="lb-kicker">{LAONA.operator} · 시제품 서비스</p>
            <h1>{SELFPROTO.name}</h1>
            <p className="lb-lead">
              만들고 싶은 기능을 검증된 모듈로 조합해 시제품을 직접 만듭니다. 두 가지 방법 중에서 고르세요.
              직접 만들다가 라오나봇과 이어서 만들 수도 있습니다.
            </p>
          </header>
          <WayCards className="lb-ways--top" />
        </div>
      </section>

      <section className="lb-hero lb-hero--bot" id="with-laonabot">
        <div className="lb-wrap lb-hero__grid">
          <div>
            <p className="lb-kicker">WITH LAONABOT · 라오나봇과 만들기</p>
            <h2 className="lb-hero__title lb-hero__title--sub">
              라오나봇과 대화로 설계하면,<br />조립할 수 있는 시제품이 도착합니다.
            </h2>
            <p className="lb-hero__desc">
              만들고 싶은 기능을 말하면 라오나봇이 검증된 모듈로 구성을 짜고,
              브레드보드 배선도와 동작하는 펌웨어까지 준비합니다.
              회로 · 코딩 · 부품 구하기, 아무것도 몰라도 됩니다.
            </p>
            <div className="lb-actions">
              {svc.ai && (
                <Link to={member?.email_verified ? "/laonabot?way=bot" : member ? "/laonabot/start?way=bot" : "/laonabot/signup"} state={member ? undefined : { from: "/laonabot/start?way=bot" }} className="lb-btn lb-btn--primary">
                  {member ? "라오나봇과 시작하기" : "무료 가입하고 시작하기"}
                </Link>
              )}
              <Link to="/laonabot/recipes" className="lb-btn">레시피 보기</Link>
            </div>
            <p className="lb-hero__note">
              {svc.ai
                ? `무료로 시작합니다 — 사용량이 ${FREE_KRW.toLocaleString("ko-KR")}원을 넘으면 이용료 ${DEPOSIT.toLocaleString("ko-KR")}원. 펌웨어 개발 · 빌드는 주문할 때 함께(기본 5만 원). 레시피 갤러리는 누구나 볼 수 있습니다.`
                : "라오나봇 AI 설계 서비스는 준비 중입니다. 그동안 '직접 만들기'로 연결도를 그리고 부품을 주문할 수 있습니다."}
            </p>
          </div>
          <div className="lb-hero__art">
            <HeroDiagram />
          </div>
        </div>
        <div className="lb-wrap lb-hero__how">
          <header className="lb-head">
            <p className="lb-kicker">HOW IT WORKS</p>
            <h3 className="lb-hero__how-title">대화에서 시제품까지, 이렇게 진행됩니다</h3>
          </header>
          <ol className="lb-steps lb-steps--6">
            {STEPS.map((s) => (
              <li key={s.no} className="lb-steps__item">
                <div className="lb-steps__link">
                  <span className="lb-steps__no">{s.no}</span>
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="lb-section">
        <div className="lb-wrap">
          <header className="lb-head lb-head--row">
            <div>
              <p className="lb-kicker">RECIPES</p>
              <h2>레시피 갤러리</h2>
              <p className="lb-lead">회원들이 직접 설계하고 배선까지 마친 시제품입니다. 내 프로젝트로 가져와 그대로, 또는 바꿔 만들 수 있습니다.</p>
            </div>
            <Link to="/laonabot/recipes" className="lb-more">전체 보기 →</Link>
          </header>
          <Recipes />
        </div>
      </section>

      <section className="lb-section lb-section--panel">
        <div className="lb-wrap">
          <header className="lb-head">
            <p className="lb-kicker">FOR</p>
            <h2>결과물이 필요한 분들을 위해 만들었습니다</h2>
          </header>
          <ul className="lb-grid4">
            {WHO.map(([t, d]) => <li key={t}><b>{t}</b><span>{d}</span></li>)}
          </ul>
        </div>
      </section>

      <section className="lb-section">
        <div className="lb-wrap">
          <header className="lb-head">
            <p className="lb-kicker">PROMISE</p>
            <h2>라오나봇이 지키는 것</h2>
          </header>
          <ul className="lb-grid4">
            {PROMISES.map(([t, d]) => <li key={t}><b>{t}</b><span>{d}</span></li>)}
          </ul>
        </div>
      </section>

      <section className="lb-section lb-section--panel">
        <div className="lb-wrap lb-wrap--narrow">
          <header className="lb-head">
            <p className="lb-kicker">FAQ</p>
            <h2>자주 묻는 질문</h2>
          </header>
          <div className="lb-faq">
            {FAQ.map(([q, a]) => (
              <details key={q} className="lb-faq__item">
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
          <p className="lb-faq__more">
            카탈로그 밖의 개발 · 양산은 <a href="/contact">아이닷메타 개발 문의</a>로 보내 주세요.
          </p>
        </div>
      </section>
    </>
  );
}
